import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useUser } from "src/shared/context/UserContext";
import { getOrder, deleteOrder } from "../services/servicesv2";
import { Order, PaginationData, UseOrdersProps } from "../services/interfacev2";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";

interface OrderParams {
  dateFilter: string;
  searchBy: string;
  startDate?: string;
  endDate?: string;
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Map URL-stored _id labels → backend numeric codes
const toApiDateFilter = (label: string): string => {
  switch (label) {
    case "LastWeek": return "7";
    case "LastMonth": return "30";
    case "180days": return "180";
    case "CustomizeDate": return "custom";
    case "Yesterday": return "yesterday";
    case "Today": return "today";
    default: return label.toLowerCase();
  }
};

const normalizeFilterOption = (raw: string): string => {
  const map: Record<string, string> = {
    "7": "LastWeek",
    "30": "LastMonth",
    "180": "180days",
    "custom": "CustomizeDate",
    "180days": "180days",
    "customizedate": "CustomizeDate",
    "today": "Today",
    "yesterday": "Yesterday",
  };
  return map[raw.toLowerCase()] ?? raw;
};

export const useOrders = ({ initialPage = 0, initialRowsPerPage = 10 }: UseOrdersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userConfigration } = useUser();

  // Read initial states from URL or fall back to defaults
  const pageFromUrl = Number(searchParams.get("pageNumber"))
    ? Number(searchParams.get("pageNumber")) - 1
    : initialPage;
  const rowsPerPageFromUrl =
    Number(searchParams.get("pageSize")) ||
    userConfigration?.tableDefaultSize ||
    initialRowsPerPage;
  const searchFromUrl = searchParams.get("searchBy") || "";
  const filterOptionFromUrl = normalizeFilterOption(
    searchParams.get("dateFilter") || "Today"
  );
  const startDateFromUrl = searchParams.get("startDate") || "";
  const endDateFromUrl = searchParams.get("endDate") || "";
  const statusFromUrl = searchParams.get("status") || "";

  const [orders, setOrders] = useState<Order[]>([]);
  const [loadings, setLoadings] = useState(false);
  const [page, setPage] = useState(pageFromUrl);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageFromUrl);
  const [searchTerm, setSearchTerm] = useState(searchFromUrl);
  const [selectedOption, setSelectedOption] = useState(filterOptionFromUrl);
  const [selectedDate, setSelectedDate] = useState(startDateFromUrl);
  const [selectedDate2, setSelectedDate2] = useState(endDateFromUrl);
  const [statusFilter, setStatusFilter] = useState(statusFromUrl);

  const [paginationData, setPaginationData] = useState<PaginationData>({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: rowsPerPageFromUrl,
  });

  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Helper to sync state to browser URL query bar
  const updateUrlParams = useCallback(
    (newParams: Record<string, string | number | undefined>) => {
      setSearchParams(
        (prevParams) => {
          const updated = new URLSearchParams(prevParams);
          Object.entries(newParams).forEach(([key, val]) => {
            if (val !== undefined && val !== "") {
              updated.set(key, String(val));
            } else {
              updated.delete(key);
            }
          });
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const getDateRange = useCallback(
    (filterOption = selectedOption) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let fromDate = new Date(today);
      let toDate = new Date(today);

      switch (filterOption) {
        case "Yesterday": {
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          fromDate = yesterday;
          toDate = yesterday;
          break;
        }
        case "Last Week": {
          const lastWeek = new Date(today);
          lastWeek.setDate(today.getDate() - 6);
          fromDate = lastWeek;
          break;
        }
        case "Last Month": {
          const lastMonth = new Date(today);
          lastMonth.setDate(today.getDate() - 29);
          fromDate = lastMonth;
          break;
        }
        case "180 days": {
          const days180 = new Date(today);
          days180.setDate(today.getDate() - 179);
          fromDate = days180;
          break;
        }
        case "CustomizeDate":
          if (selectedDate && selectedDate2) {
            fromDate = new Date(selectedDate);
            toDate = new Date(selectedDate2);
          }
          break;
      }

      return {
        startDate: formatDate(fromDate),
        endDate: formatDate(toDate),
      };
    },
    [selectedDate, selectedDate2, selectedOption]
  );

  const getDateRangeForExport = useCallback(
    (filterOption?: string) => {
      const { startDate, endDate } = getDateRange(filterOption);
      return { from: startDate, to: endDate };
    },
    [getDateRange]
  );

  const fetchOrders = useCallback(
    async (
      pageNumber = page + 1,
      pageSize = rowsPerPage,
      search = searchTerm,
      filterOption = selectedOption,
      status = statusFilter
    ) => {
      try {
        setLoadings(true);
        const isSearching = Boolean(search.trim());

        if (!isSearching && filterOption === "CustomizeDate" && (!selectedDate || !selectedDate2)) {
          setOrders([]);
          setPaginationData({ totalRecords: 0, totalPages: 0, currentPage: 1, pageSize });
          return;
        }

        // API value derived from the option label
        let apiDateFilter = "";
        let startDateVal: string | undefined = undefined;
        let endDateVal: string | undefined = undefined;

        if (!isSearching) {
          apiDateFilter = toApiDateFilter(filterOption);

          // ✅ Only pass startDate and endDate when dateFilter is CustomizeDate
          if (filterOption === "CustomizeDate") {
            startDateVal = selectedDate;
            endDateVal = selectedDate2;
          }
        }

        // ✅ Store the OPTION LABEL (matching DATE_OPTIONS._id) in the URL
        updateUrlParams({
          pageNumber,
          pageSize,
          searchBy: search,
          dateFilter: filterOption, // e.g. "LastMonth", "LastWeek"
          startDate: startDateVal,
          endDate: endDateVal,
          status,
        });

        const params: OrderParams = {
          dateFilter: apiDateFilter, // e.g. "30", "7"
          searchBy: search,
          ...(startDateVal && { startDate: startDateVal }),
          ...(endDateVal && { endDate: endDateVal }),
        };

        const response = await getOrder(
          "",
          pageNumber,
          pageSize,
          params.startDate ?? "",
          params.endDate ?? "",
          "",
          "",
          "",
          status || "",
          params
        );

        if (response.data?.result) {
          setOrders(response.data.result);
          setPaginationData({
            totalRecords: response.data.totalRecords || 0,
            totalPages: response.data.totalPages || 0,
            currentPage: response.data.currentPage || 1,
            pageSize: response.data.pageSize || pageSize,
          });
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        notifyError("Failed to fetch orders");
      } finally {
        setLoadings(false);
      }
    },
    [page, rowsPerPage, searchTerm, selectedOption, selectedDate, selectedDate2, statusFilter, updateUrlParams]
  );

  const handleDeleteOrder = useCallback(
    async (orderId: string) => {
      try {
        await deleteOrder(orderId);
        notifySuccess("Order deleted successfully");
        fetchOrders(page + 1, rowsPerPage, searchTerm);
      } catch {
        notifyError("Failed to delete order");
      }
    },
    [fetchOrders, page, rowsPerPage, searchTerm]
  );

  const handleSearch = (value: string) => {
    const searchValue = value.trim();
    setSearchTerm(searchValue);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(0);
      fetchOrders(1, rowsPerPage, searchValue, selectedOption);
    }, 500);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchOrders(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchOrders(1, newRows, searchTerm, selectedOption);
  };

  const handleFilterChange = (value: string) => {
    setSelectedOption(value);
    setPage(0);

    if (value !== "CustomizeDate" && value !== "custom") {
      setSelectedDate("");
      setSelectedDate2("");
      fetchOrders(1, rowsPerPage, searchTerm, value);
    } else {
      setOrders([]);
      setPaginationData({ totalRecords: 0, totalPages: 0, currentPage: 1, pageSize: rowsPerPage });
    }
  };

  const applyCustomDateFilter = () => {
    if (!selectedDate || !selectedDate2) {
      notifyWarning("Please select both start and end dates");
      return;
    }
    if (new Date(selectedDate) > new Date(selectedDate2)) {
      notifyWarning("Start date cannot be after end date");
      return;
    }
    setSelectedOption("CustomizeDate");
    setPage(0);
    fetchOrders(1, rowsPerPage, searchTerm, "CustomizeDate");
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(0);
    fetchOrders(1, rowsPerPage, searchTerm, selectedOption, status);
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    orders, loadings, page, rowsPerPage, searchTerm, selectedOption, selectedDate, selectedDate2, statusFilter, paginationData,
    handleSearch, handleChangePage, handleChangeRowsPerPage, handleFilterChange, applyCustomDateFilter, handleStatusFilterChange,
    setSelectedDate, setSelectedDate2, fetchOrders, getDateRange, getDateRangeForExport, handleDeleteOrder,
  };
};